const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

export function getApiUrl() {
  return API_URL;
}

// ── Silent token refresh ───────────────────────────────────────────────────
// Mirrors the mobile client (`mobile/src/api/client.ts`): on a 401 for an
// *authenticated* request we transparently POST /api/auth/refresh, swap in the
// new access (and rotated refresh) token, and retry the original request ONCE.
// Only when refresh itself fails do we fall back to the hard logout below.
// `refreshInFlight` makes concurrent 401s share a single refresh call so a
// burst of requests can't race the refresh-token rotation.
let refreshInFlight = null;

function tryRefresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json().catch(() => ({}));
      if (!data || !data.access_token) return false;
      localStorage.setItem("access_token", data.access_token);
      if (data.refresh_token) {
        localStorage.setItem("refresh_token", data.refresh_token);
      }
      return true;
    } catch (_) {
      return false;
    }
  })();
  // Reset the gate once this refresh settles (success or failure).
  refreshInFlight.finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

let authFailureHandled = false;
function handleAuthFailure(message) {
  if (authFailureHandled) return;
  authFailureHandled = true;
  setTimeout(() => { authFailureHandled = false; }, 1500);

  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
  sessionStorage.setItem("auth_error", message || "Your session has expired. Please log in again.");
  // Defer to next tick so any in-flight React render can finish before we
  // tear down the user state and reset the hash route.
  setTimeout(() => {
    window.dispatchEvent(new Event("vs_auth_error"));
    if (window.location.hash && window.location.hash !== "#/") {
      window.location.hash = "#/";
    }
  }, 0);
}

export async function apiFetch(path, { method = "GET", body, token, authRequired = false, formData, signal, cache, _isRetry = false } = {}) {
  const headers = {};
  if (!formData) headers["Content-Type"] = "application/json";
  const finalToken = token || (authRequired ? localStorage.getItem("access_token") : null);
  if (finalToken) headers.Authorization = `Bearer ${finalToken}`;

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: formData ? formData : body ? JSON.stringify(body) : undefined,
    signal,
    ...(cache ? { cache } : {}),
  });

  if (res.status === 401) {
    // For an authenticated request, try a one-shot silent refresh + retry
    // before giving up — the access token may simply have expired while the
    // refresh token is still valid. Skip for the refresh endpoint itself and
    // for already-retried calls to avoid recursion.
    if (finalToken && !_isRetry && path !== "/api/auth/refresh") {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return apiFetch(path, {
          method,
          body,
          // use the freshly-refreshed token, not the (now stale) passed one
          token: localStorage.getItem("access_token"),
          authRequired,
          formData,
          signal,
          cache,
          _isRetry: true,
        });
      }
    }
    handleAuthFailure("Your session is invalid or has expired. Please log in again.");
    const err = new Error("Invalid token");
    err.status = 401;
    throw err;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data && data.detail ? data.detail : "Request failed";
    // Surface structured error payloads (e.g. {code, existing}) so
    // callers can inspect them — see /api/directory/contacts dedup.
    const msg =
      typeof detail === "string"
        ? detail
        : detail && detail.message
        ? detail.message
        : JSON.stringify(detail);
    const err = new Error(msg);
    err.status = res.status;
    err.detail = detail;
    err.body = data;
    throw err;
  }
  return data;
}
